import React from 'react';
import { YearData } from '../pensionCalculator.ts';

interface CalculationBreakdownProps {
  year: YearData;
}

export const CalculationBreakdown: React.FC<CalculationBreakdownProps> = ({ year }) => {
  return (
    <div className="space-y-4">
      <h4 className="font-medium text-gray-900">Calculation Breakdown</h4>
      
      {/* Tapered Allowance Calculation */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <h5 className="font-medium text-gray-800 text-sm">Annual Allowance</h5>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Standard allowance:</span>
            <span className="font-medium">£40,000</span>
          </div>
          
          {year.adjustedIncome > 240000 && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Threshold income:</span>
                <span className="font-medium">£{year.thresholdIncome.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Adjusted income:</span>
                <span className="font-medium">£{year.adjustedIncome.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Excess over £240k:</span>
                <span className="font-medium">£{Math.max(0, year.adjustedIncome - 240000).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Taper reduction (£1 per £2):</span>
                <span className="font-medium text-red-600">-£{Math.max(0, Math.floor((year.adjustedIncome - 240000) / 2)).toLocaleString()}</span>
              </div>
              <hr className="border-gray-300" />
            </>
          )}
          
          <div className="flex justify-between font-semibold">
            <span>Tapered allowance:</span>
            <span className="text-blue-600">£{year.taperedAllowance.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Carry Forward Breakdown */}
      {(year.carryForwardAvailableFromPrevious > 0 || year.carryForwardUsed > 0) && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h5 className="font-medium text-gray-800 text-sm">Carry Forward</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Available from previous years:</span>
              <span className="font-medium text-green-600">£{year.carryForwardAvailableFromPrevious.toLocaleString()}</span>
            </div>
            
            {year.carryForwardUsed > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">Used this year:</span>
                <span className="font-medium text-blue-600">£{year.carryForwardUsed.toLocaleString()}</span>
              </div>
            )}
            
            <div className="flex justify-between">
              <span className="text-gray-600">Total allowance available:</span>
              <span className="font-medium">£{year.canUseThisYear.toLocaleString()}</span>
            </div>
          </div>

          {/* Detailed carry forward usage breakdown */}
          {year.carryForwardUsedBreakdown && year.carryForwardUsedBreakdown.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-xs text-gray-600 mb-2">Used from:</p>
              <div className="space-y-1">
                {year.carryForwardUsedBreakdown.map((usage, index) => (
                  <div key={index} className="flex justify-between text-xs">
                    <span className="text-gray-500">{usage.fromYear}:</span>
                    <span className="font-medium">£{usage.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-700">Total allowance available:</span>
            <span className="font-semibold">£{year.canUseThisYear.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-700">Your contribution:</span>
            <span className="font-semibold">£{year.contribution.toLocaleString()}</span>
          </div>
          <hr className="border-blue-300" />
          <div className="flex justify-between font-semibold text-base">
            <span>Remaining for carry forward:</span>
            <span className="text-green-600">£{year.carryForwardAvailable.toLocaleString()}</span>
          </div>
          {year.shortfall > 0 && (
            <div className="flex justify-between font-semibold text-base">
              <span>Shortfall (excess):</span>
              <span className="text-red-600">£{year.shortfall.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};